'use client';

import DetailContent from '@/components/sections/builder/elements/body/blog-home/post-list/detail-content';
import {
  BlogHomeElementDataType,
  BlogHomeListElementDataType,
} from '@/components/sections/builder/elements/body/blog-home/type';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import ModalSelectBlog from '@/components/sections/builder/settings/modal-select-blog';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useBlogsList } from '@/hooks/api/storefront/blogs';
import useModal from '@/hooks/useModal';
import { cn } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import { AdminBlogListResponse } from '@/types/http';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronDown, Columns3, ExternalLinkIcon, RefreshCcwIcon, Trash2Icon } from 'lucide-react';
import { useParams } from 'next/navigation';
import React from 'react';
import { shallow } from 'zustand/shallow';

const defaultData: BlogHomeListElementDataType = {
  name: 'Blog Post',
  source: '',
  limit: 4,
  showImage: true,
  showDate: true,
  showAuthor: true,
  showCategory: true,
  showContent: true,
  showShadow: true,
  rounded: 10,
  gap: 1,
};

export const ElementContent = ({ element }: ElementProps) => {
  const { category } = useParams<{ category: string }>();
  const data = (element.data || defaultData) as BlogHomeListElementDataType;
  const parentData = element.parentData as BlogHomeElementDataType;
  const autoplay = parentData.autoplay ? true : false;

  const { data: blogs } = useBlogsList(data.source, {
    enabled: !!data.source,
  });

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const delay = parentData.autoplay ? (parentData?.changeSlidesEvery || 5) * 1000 : 0;
  const plugin = React.useRef(
    Autoplay({
      delay: delay,
      playOnInit: autoplay,
    })
  );

  return (
    <>
      <div
        className={cn(
          'blog-post-list-padding theme_layout_space_grid_horizontal theme_layout_space_grid_vertical place-content-center',
          !parentData.enableCarousel ? 'grid' : 'hidden',
          `grid-cols-${data.columnsMobile} md:grid-cols-${data.limit}`
        )}
      >
        {blogs?.map((blog: any, i: number) => (
          <DetailContent
            key={blog.id}
            data={data}
            blog={blog}
            element={element}
            index={i}
            builder={builder}
            category={category}
          />
        ))}
      </div>
      <div
        className={cn(
          'blog-post-list-padding theme_layout_space_grid_horizontal theme_layout_space_grid_vertical place-content-center',
          !parentData.enableCarousel ? 'hidden' : 'block',
          `grid-cols-${data.columnsMobile} md:grid-cols-${data.limit}`
        )}
      >
        <Carousel
          className='max-w-full'
          plugins={autoplay ? [plugin.current] : []}
          opts={{ loop: true }}
        >
          <CarouselContent>
            {blogs?.map((blog: any, i) => (
              <CarouselItem className='carousel-item-column' key={blog?.id}>
                <DetailContent
                  data={data}
                  blog={blog}
                  element={element}
                  index={i}
                  builder={builder}
                  category={category}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className='left-4 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
          <CarouselNext className='right-4 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
        </Carousel>
      </div>
      <style jsx global>{`
        @media (min-width: 768px) {
          .carousel-item-column {
            flex-basis: ${100 / data.limit}% !important;
          }
        }
        .carousel-item-column {
          flex-basis: ${100 / (data.columnsMobile || 1)}%;
        }
      `}</style>
      <style jsx>
        {`
          @media (min-width: 768px) {
            .blog-post-list-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }

          .theme_blog_card {
            background-color: ${parentData.colorSchemeBlogHome?.background};
            color: ${parentData.colorSchemeBlogHome?.text};
          }
          .post-gap {
            margin-right: ${data.gap || 0}px;
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Columns3;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
      setElementData: state.setElementData,
    }),
    shallow
  );

  const data = element.data as BlogHomeListElementDataType;

  const test = element;

  const selectBlogsModal = useModal<Partial<AdminBlogListResponse>>();

  if (data.source === '') {
    return (
      <>
        <Button
          type='button'
          variant='outline'
          className='flex w-full gap-x-2 bg-white'
          onClick={() => {
            selectBlogsModal.open();
          }}
        >
          <span>Select Blogs</span>
        </Button>
        <ModalSelectBlog
          openModal={selectBlogsModal.isShow}
          setOpenModal={(e) => !e && selectBlogsModal.setIsShow(e)}
          element={element}
        />
      </>
    );
  }

  return (
    <div>
      <div className='mb-4 font-bold'>Blog Source</div>

      <div className='rounded-md bg-gray-50 py-4'>
        <div className='mb-4 flex gap-x-2 border-b border-white px-4 pb-3'>
          <div>{data.source}</div>
          <ExternalLinkIcon className='h-4 w-4' />
        </div>
        <div className='px-4'>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button type='button' variant='outline' className='flex w-full gap-x-2 bg-white'>
                <span>Change</span>
                <ChevronDown className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='w-full'>
              <DropdownMenuItem
                onClick={() => {
                  selectBlogsModal.open();
                }}
              >
                <RefreshCcwIcon className='mr-2 h-4 w-4' />
                <div>Change Blog</div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  builder.setChildData(element.parentId as string, element.id, {
                    source: '',
                  });
                }}
              >
                <Trash2Icon className='mr-2 h-4 w-4' />
                <div>Remove Blog</div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ModalSelectBlog
        openModal={selectBlogsModal.isShow}
        setOpenModal={(e) => !e && selectBlogsModal.setIsShow(e)}
        element={test}
      />
      <div className='mb-2 mt-4 text-xs'>Number of columns on desktop</div>
      <InputSlider
        value={data.limit || 1}
        max={5}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            limit: val,
          })
        }
      />
      <div className='mb-2 mt-4 text-xs'>Number of columns on mobile</div>
      <InputSlider
        value={data.columnsMobile || 3}
        max={2}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            columnsMobile: val,
          })
        }
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showImage'
          aria-label='Select'
          className='mx-2'
          checked={data.showImage}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showImage: !!checked,
            });
          }}
        />
        <label htmlFor='showImage'>Show image</label>
      </div>

      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showAuthor'
          aria-label='Select'
          className='mx-2'
          checked={data.showAuthor}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showAuthor: !!checked,
            });
          }}
        />
        <label htmlFor='showAuthor'>Show author</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showDate'
          aria-label='Select'
          className='mx-2'
          checked={data.showDate}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showDate: !!checked,
            });
          }}
        />
        <label htmlFor='showDate'>Show date</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showContent'
          aria-label='Select'
          className='mx-2'
          checked={data.showContent}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showContent: !!checked,
            });
          }}
        />
        <label htmlFor='showDate'>Show Content</label>
      </div>
      <div className='mb-4 mt-4 font-bold'>Gap Post</div>
      <InputSlider
        value={data.gap || 0}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            gap: val,
          })
        }
      />
      {/* temporary hide */}
      {/* <div className='flex mt-4 gap-x-1'>
        <Checkbox
          id='showShadow'
          aria-label='Select'
          className='mx-2'
          checked={data.showShadow}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showShadow: !!checked,
            });
          }}
        />
        <label htmlFor='showShadow'>Show shadow</label>
      </div> */}
      <PaddingSetting
        value={data.padding}
        hideLeft={true}
        hideRight={true}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />
    </div>
  );
}

const BlogHomeList = {
  type: 'blog_home_list',
  group: 'blog_home',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default BlogHomeList;
